/**
 * export-task.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, Input } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";

import { UserService } from "../auth/identity/user.service";
import { ColumnDescription }
    from "../server/messages/export/start-data-export-request";

import { Aggregation } from "./aggregation";
import { AggregationService } from "./aggregation.service";
import { Aggregator } from "./aggregator";
import { DataExport } from "./data-export";
import { ToolboxTaskComponent } from "./toolbox-task.component";
import { StdVariable, Variable } from "./typed-parameter";
import { FilterNode, OperatorNode } from "./filter-node";
import { Operators, ClientOnlyMultiAnd } from "./operators";
import { ModalService } from "app/common/modal.service";

/**
 * The {@link ExportTaskComponent} shows a current {@link DataExport} task in
 * either the configuring, running, or completed stages. It provides the
 * majority of the interface for data exports, including key actions such as
 * configuring, running, and downloading.
 */
@Component({
    selector: "export-task",
    template: `
<div
    #panel
    class="panel panel-default">
    <div class="panel-heading" (click)="panel.closed = !panel.closed">
        <h3 class="panel-title">
            <span
                class="glyphicon"
                [class.glyphicon-expand]="panel.closed"
                [class.glyphicon-collapse-down]="!panel.closed">
            </span>
            <a
                title="Rename this query..."
                (click)="$event.stopPropagation(); renameTask()">
                {{task.title}}
            </a>
            <small>
                (algorithm:
                <span class="text-success">{{task.algorithm}}</span>,
                group:
                <span class="text-info">{{task.group}}</span>)
            </small>
            <button
                type="button"
                class="btn btn-xs btn-danger pull-right"
                (click)="release()">
                Remove
            </button>
        </h3>
    </div>
    <div class="panel-body collapse" [class.in]="!panel.closed">
        <h4>Options</h4>
        <div *ngIf="true || !task.isStarted" class="form-group">
            <template [ngIf]="!task.isStarted">
            <p>
                Use the boxes below to add columns to your exported
                dataset. Only the variables you select will be shown in the
                final data.
            </p>
            <p>
                When adding a variable whose value is a list or part of a
                table, you must select a <em>summarization method</em>. By
                selecting <code>none</code>, the members of that list or
                table will be repeated, either within a single cell using
                a text delimiter, or in duplicate rows. (This export type will
                do the {{ task.parent.allowsInlining ? "former" : "latter"}}.)
                The other values will cause the export to run that calculation
                over all members of the list. As such, the <code>sum</code> and
                <code>average</code> summarizations can only be used on numeric
                data.
            </p>
            </template>
            <column-selection-input
                [source]="task.columnOptions"
                [target]="task.columnSelection"
                [optionsSorted]="true"
                [maxIterators]="maxIterators"
                [controlArray]="task.membershipControl">
            </column-selection-input>
        </div>
        <div *ngIf="false && task.isStarted" class="form-group">
            <label>
                Columns
                <span class="badge">{{task.columns.length}}</span>
            </label>
            <div class="row">
                <div
                    *ngFor="let cols of task.columns | segments:4"
                    class="col-sm-3">
                    <ul>
                        <li *ngFor="let col of cols">
                            {{col.columnName || col.name}}
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-sm-4">
                <div class="form-group">
                    <label>Column settings:</label>
                    <div class="radio">
                        <label>
                            <input
                                type="radio"
                                [name]="'headers' + task.id"
                                [value]="true"
                                [formControl]="task.headersControl"
                                checked>
                            Use column headers
                        </label>
                    </div>
                    <div class="radio">
                        <label>
                            <input
                                type="radio"
                                [name]="'headers' + task.id"
                                [value]="false"
                                [formControl]="task.headersControl">
                            No headers (start with data)
                        </label>
                    </div>
                </div>
            </div>
            <div class="col-sm-4">
                <div class="form-group">
                    <label>Timestamp format:</label>
                    <div class="radio">
                        <label>
                            <input
                                type="radio"
                                [name]="'date' + task.id"
                                [value]="true"
                                [formControl]="task.excelDatesControl"
                                checked>
                            Date only (Excel-compliant)
                        </label>
                    </div>
                    <div class="radio">
                        <label>
                            <input
                                type="radio"
                                [name]="'date' + task.id"
                                [value]="false"
                                [formControl]="task.excelDatesControl">
                            Full ISO-8601 timestamp
                        </label>
                    </div>
                </div>
            </div>
            <div class="col-sm-4">
                <div class="form-group">
                    <label>Collection filter behavior:</label>
                    <div class="radio">
                        <label>
                            <input
                                type="radio"
                                [name]="'collFilterBehavior' + task.id"
                                [value]="true"
                                [formControl]="task.dropCollFilterEmptiesControl"
                                checked>
                            Remove records that have no matching collection entries 
                        </label>
                    </div>
                    <div class="radio">
                        <label>
                            <input
                                type="radio"
                                [name]="'collFilterBehavior' + task.id"
                                [value]="false"
                                [formControl]="task.dropCollFilterEmptiesControl">
                            Keep all records (collection filters will only remove sub-entries)
                        </label>
                    </div>
                </div>
            </div>

            <!-- at col-4, this is the start of the 2nd row -->
            <div
                *ngIf="task.parent?.customizableCollectionDelimiter || true"
                class="col-sm-4">
                <div class="form-group">
                    <label>Custom delimiter:</label>
                    <input
                        type="text"
                        class="form-control"
                        [formControl]="task.delimiterControl">
                </div>
            </div>
            <div class="col-sm-4">
                <div class="form-group">
                    <label>Execution note (optional):</label>
                    <input
                        type="text"
                        class="form-control"
                        [formControl]="task.labelControl">
                </div>
            </div>
            <div class="col-sm-4">
                <div class="form-group">
                    <label>Target:</label>
                    <div class="form-control" disabled>
                        {{task.algorithm}} on {{task.group}}
                    </div>
                </div>
            </div>
        </div>
        <div
            *ngIf="!(task.isStarted && task.filter === null)"
            class="form-group">
            <label class="h3">
                Filter
            </label>
            <span class="btn-group">
                <button
                    *ngIf="!task.isStarted"
                    type="button"
                    class="btn btn-xs btn-default"
                    (click)="resetFilter(true)"
                    [disabled]="task.isStarted">
                    {{task.filter === null ? "Create" :
                        (simpleFilter ? "Reset" : "Reset in simple mode")}}
                </button>
                <button
                    *ngIf="!task.isStarted"
                    type="button"
                    class="btn btn-xs btn-info"
                    (click)="resetFilter()"
                    [disabled]="task.isStarted">
                    {{task.filter === null ? "Create Advanced" :
                        (simpleFilter ? "Reset in advanced mode" : "Reset")}}
                </button>
                <button
                    *ngIf="!task.isStarted && task.filter !== null"
                    type="button"
                    class="btn btn-xs btn-danger"
                    (click)="removeFilter()"
                    [disabled]="task.isStarted">
                    Remove
                </button>
            </span>
            <filter-node
                *ngIf="task.filter !== null && !task.isStarted && !simpleFilter"
                [node]="task.filter"
                [variables]="task.filterOptions"
                [root]="true">
            </filter-node>
            <simple-filter-node
                *ngIf="task.filter !== null && !task.isStarted && simpleFilter"
                [node]="task.filter"
                [variables]="task.filterOptions">
            </simple-filter-node>
            <div
                style="margin-left: 2em"
                *ngIf="task.filterString"
                [innerHtml]="task.filterString">
            </div>
        </div>
        
        <!-- ACTIONS BAR -->
        <div class="row">
            <div class="col-sm-4">
                <button
                    class="btn btn-success p100"
                    (click)="run()"
                    [disabled]="task.isStarted && !task.isComplete">
                    <span
                        class="glyphicon"
                        [class.glyphicon-play]="!task.isComplete"
                        [class.glyphicon-repeat]="task.isComplete">
                    </span>
                    {{ task.isComplete ? "Re-run" : "Run" }}
                </button>
            </div>
            <div class="col-sm-4">
                <button
                    *ngIf="!task.isPaused"
                    class="btn btn-info p100"
                    (click)="pause(true)"
                    [disabled]="!task.isRunning">
                    <span class="glyphicon glyphicon-pause"></span>
                    Pause
                </button>
                <button
                    *ngIf="task.isPaused"
                    class="btn btn-info p100"
                    (click)="pause(false)">
                    <span class="glyphicon glyphicon-step-forward"></span>
                    Resume
                </button>
            </div>
            <div class="col-sm-4">
                <button
                    class="btn btn-danger p100"
                    (click)="cancel()"
                    [disabled]="!(task.isRunning || task.isPaused)">
                    <span class="glyphicon glyphicon-remove"></span>
                    Cancel
                </button>
            </div>
            <!--<button
                class="btn btn-default"
                (click)="getProgress()"
                [disabled]="task.isComplete || !task.isStarted">
                <span class="glyphicon glyphicon-tasks"></span>
                Request Progress
            </button>-->
            <div class="col-sm-6">
                <button
                    class="btn btn-default p100"
                    (click)="saveSchema()"
                    [disabled]="!task.isComplete">
                    <span class="glyphicon glyphicon-pushpin"></span>
                    Save as Template
                </button>
            </div>
            <div class="col-sm-6">
                <button
                    class="btn btn-default p100"
                    (click)="duplicate()"
                    [disabled]="false">
                    <span class="glyphicon glyphicon-copy"></span>
                    Duplicate
                </button>
            </div>
        </div>
    </div>

    <div
        *ngIf="task.isStarted"
        class="panel-body collapse"
        [class.in]="!panel.closed">
        <task-progress [task]="task"></task-progress>
    </div>

    <div
        *ngIf="task.isComplete"
        class="panel-body collapse reports"
        [class.in]="!panel.closed">
        <h3>Results</h3>
        <div class="row">
            <template [ngIf]="task.downloadFile">
                <div class="col-md-3 col-sm-6">
                    <label>File MIME type</label>
                    <p>{{task.downloadFile.type}}</p>
                </div>
                <div class="col-md-3 col-sm-6">
                    <label>File bytes</label>
                    <p>{{task.downloadFile.file.size | bytes}}</p>
                    <p
                        *ngIf="users.debugMode && (task.downloadFile.file.size !== task.expectedFilesize)"
                        class="debug-only text-danger">
                        {{task.expectedFilesize | bytes}} expected
                    </p>
                </div>
                <div *ngIf="users.debugMode" class="col-md-3 col-sm-6 debug-only">
                    <label>Buffer count</label>
                    <p>
                        {{task.downloadFile.bufferCount}}
                        <span
                            [class.text-danger]="task.downloadFile.emptyCount">
                            (+{{task.downloadFile.emptyCount}} empty)
                        </span>
                    </p>
                </div>
                <div *ngIf="users.debugMode" class="col-md-3 col-sm-6 debug-only">
                    <label>File checksum</label>
                    <p>{{task.checksumHex}}</p>
                </div>
            </template>
            <template [ngIf]="task.downloadURL">
                <div class="col-sm-6">
                    <label>Filename:</label>
                    <input
                        type="text"
                        class="form-control"
                        [formControl]="task.filenameControl">
                </div>
                <div class="col-sm-6">
                    <a
                        class="btn btn-success p100 align-with-labels"
                        [href]="task.downloadSafeURL"
                        [download]="task.getDownloadName()"
                        [class.disabled]="task.filenameControl.invalid">
                        <span class="glyphicon glyphicon-save"></span>
                        Download Export File
                    </a>
                </div>
            </template>
        </div>
    </div>
</div>
    `,
    styles: [
        `
        .single-result th, .single-result td {
            font-size: 1.6em;
        }
        .panel-body + .panel-body {
            border-top: 1px solid #ddd;
        }
        .panel h3, .panel h4 {
            margin-top: 0;
        }
        .frequency-report table {
            padding: 10px;
            margin: auto;
        }
        .reports td, .reports th {
            text-align: center;
        }
        .context-result th, .context-result h4 {
            text-transform: capitalize;
        }
        .btn-group {
            vertical-align: text-bottom;
        }
        `
    ]
})
export class DataExportTaskComponent extends ToolboxTaskComponent
{
    /**
     * The single {@link DataExport} task this component represents
     */
    @Input()
    public task : DataExport;

    public get simpleFilter () : boolean
    {
        return (this.task
            && this.task.filter
            && this.task.filter instanceof OperatorNode
            && this.task.filter.operator instanceof ClientOnlyMultiAnd);
    }
    public set simpleFilter (_ : boolean) { /* no set */ }

    public get maxIterators () : number
    {
        return (
            (this.task.parent.allowsInlining
                || !this.task.parent.useRecordRepetition)
            ? Number.MAX_SAFE_INTEGER : 1);
    }

    constructor (
        public service : AggregationService,
        public modals : ModalService,
        public users : UserService
    )
    {
        super(service, modals);
    }

    /**
     * Convert the selected column variables to column descriptions, as accepted
     * by the export agent.
     */
    protected processColumns () : void
    {
        console.log(this.task.columns);
        this.task.columns = this.task.columnSelection.map(
            (v : StdVariable & any) => Object.assign({
                name: v.name,
                columnName: (v.displayName || ""),
                        summarization:
                            (v.summarization ? v.summarization.name : "none")
                    },
                    v.filter ? {
                        filterTree: (v.filter as FilterNode).marshal()
                    } : {}
                )
        );
    }

    /**
     * Attempt to run the export.
     *
     */
    public run () : void
    {
        if (this.task.columnSelection.length === 0)
        {
            return alert("You must add at least one column to your export");
        }
        if (this.task.parent.customizableCollectionDelimiter
            && this.task.delimiterControl.invalid)
        {
            return alert("You must provide a value for the list delimiter");
        }
        this.processColumns();
        if (this.task.isComplete)
        {
            // If the user clicks run again after it has previously been deemed
            // complete, we must erase the past progress data.
            this.task.clearProgress();
        }
        this.service.run(this.task);
    }

    public duplicate () : void
    {
        this.service.duplicateExport(this.task);
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/aggregation/export-task.component.ts